import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ReactPlayer from "react-player";
import {useDispatch, useSelector} from "react-redux";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CommentInfo from "./component/CommentInfo";
import SideQuestions from "./component/SideQuestions";
import PowerPointViewer from "./component/PowerPointViewer";
import RctSectionLoader from "Components/RctSectionLoader/RctSectionLoader";
import { NotificationManager } from "react-notifications";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InstructionModal from "Routes/instructions";
import NextModuleModal from './NextModuleModal';
import * as Apis from 'Api';
import { setTestObjInfo } from "Store/Actions";
import { logMainAndSubActivitiesEnd } from "../../services/AuditDataLogger";

const LBLTModuleList = ['LBLT2202', 'LBLT22 04', 'LBLT22 01', 'LBLT22 03'];

function VideoView() {
    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();
    const dispatch = useDispatch();
    const sideQuestionRef = useRef();
    const playerRef = useRef();
    const playedSeconds = useRef();
    const isLogin = useSelector((state) => state.authUser.isLogin);
    const [testSetId, setTestSetId] = useState(params.test_sets_id);
    const [testCaseId, setTestCaseId] = useState(params.test_cases_id);
    const [attemptId, setAttemptId] = useState(params.attempts_id);
    const [testSetInfo, setTestSetInfo] = useState({});
    const [testCaseInfo, setTestCaseInfo] = useState({});
    const [complete, setComplete] = useState(true);
    const [allowSkip, setAllowSkip] = useState(false);
    const [isVideoEnd, setIsVideoEnd] = useState(false);
    const [playing, setPlaying] = useState(true);
    const [loading, setLoading] = useState(true);
    const [nextTestSetInfo, setNextTestSetInfo] = useState(null);
    const [isShowInstructionModal, setIsShowInstructionModal] = useState(false);
    const [isNextModuleModalOpen, setIsNextModuleModalOpen] = useState(false);
    const [isEmptyQuizTruth, setIsEmptyQuizTruth] = useState(false);
    const [testSetsDetailInfo, setTestSetsDetailInfo] = useState({});

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        Promise.all([
            Apis.testCasesViewInfo(testCaseId),
            Apis.attemptsDetail(attemptId, testCaseId),
            Apis.attemptsCompletedList(testSetId),
            Apis.getAttemptQuizAnswer(attemptId, testCaseId, false),
            Apis.testSetsDetail(testSetId)
        ]).then(([testCaseViewInfo, attemptsDetail, completeList, quizAnswer, testSetsDetail]) => {
            setIsEmptyQuizTruth(!Object.keys(quizAnswer).length)
            setTestSetInfo(attemptsDetail.test_sets);
            setTestCaseInfo(testCaseViewInfo);
            setComplete(attemptsDetail.complete);
            setNextTestSetInfo(attemptsDetail?.nextTestSetInfo);
            setTestSetsDetailInfo(testSetsDetail);
            // setAllowSkip(completeList.attempts.filter((v) => v.complete).length > 0);
            setAllowSkip(true);
            setLoading(false);
            dispatch(setTestObjInfo(testCaseViewInfo.modalities, { id: testCaseId }, attemptsDetail));
            const fileExt = attemptsDetail.test_sets.test_set_discussion.split('.').pop();
            if (fileExt === 'ppt' || fileExt === 'pptx') {
                setAllowSkip(true);
            }
        }).catch((e) => {
            const statusCode = e.response ? e.response.status : e.statusCode;
            if (statusCode === 403 && !isLogin) {
                navigate(
                    `/signin?redirect=${encodeURIComponent(location.pathname + location.search)}`,
                    { replace: true }
                );
            } else {
                NotificationManager.error(e.response ? e.response.data.error.message : e.message);
            }
            setLoading(false);
        });
    }


    const handleVideoProgress = (e) => {
        if (!allowSkip && (e.playedSeconds - playedSeconds.current) > 2) {
            playerRef.current.seekTo(parseFloat(playedSeconds.current), 'seconds');
        } else {
            playedSeconds.current = e.playedSeconds;
        }
        if (sideQuestionRef.current && sideQuestionRef.current.handleVideoProgress) {
            sideQuestionRef.current.handleVideoProgress(e.playedSeconds);
        }
        const playSecond = Math.floor(playedSeconds.current);
        const duration = Math.floor(playerRef.current.getDuration());
        if (playSecond % 2 === 0 || playSecond === duration) Apis.attemptsUpdateComment(attemptId, { duration: duration, played: playSecond }).then(() => null);
    }

    const handleVideoSeek = (seekTime) => {
        if (!allowSkip && (seekTime - playedSeconds.current) > 0.1) {
            playerRef.current.seekTo(parseFloat(playedSeconds.current), 'seconds');
        }
    }

    const validateForNext = () => {
        let valid = true;
        if (testCaseInfo?.modalities?.modality_type === 'presentations') {
            return true;
        } else if (!allowSkip && testCaseInfo?.modalities?.modality_type !== 'interactive_video') {
            valid = false;
            NotificationManager.error('You have to watch the video until the end.');
        } else if (!complete) {
            if (['video_lecture', 'interactive_video'].indexOf(testCaseInfo?.modalities?.modality_type) !== -1) {
                if (sideQuestionRef.current && sideQuestionRef.current.checkQuestionValidate) {
                    valid = sideQuestionRef.current.checkQuestionValidate();
                } else {
                    console.error('can not find question validation function');
                    valid = false;
                }
            }
        }
        return valid;
    }

    const onFinish = () => {
        if (validateForNext()) {
            logMainAndSubActivitiesEnd("Submit");
            Apis.attemptsFinishTest(attemptId, window.screen.width, window.screen.height).then((nextStep) => {
                if (nextStep === 'home') {
                    nextTestSetInfo ? setIsNextModuleModalOpen(true) : navigate('/main');
                } else {
                    navigate('/main/attempt/' + attemptId + '/' + nextStep + '?from=test');  // go to scores tab
                }
            }).catch((e) => {
                console.warn(e.response ? e.response.data.error.message : e.message);
            });
        }
    }

    const onStartNextModule = (subType) => {
        Apis.attemptsStart(nextTestSetInfo.testSetId, subType, nextTestSetInfo.seriesId).then(attempt => {
            let path;
            if (attempt.progress === 'test' || attempt.progress === 'undefined') {
                path = '/video-view/' + attempt.test_set_id + '/' + attempt.id + '/' + attempt.current_test_case_id;
            } else {
                path = '/main/attempt/' + attempt.id + '/' + attempt.progress;
            }
            setIsNextModuleModalOpen(false);
            window.location.href = path;
        });
    }

    const onNextModuleModalClose = () => {
        setIsNextModuleModalOpen(false);
        navigate('/main');
    }

    const handleVideoEnded = () => {
        setAllowSkip(true);
        setIsVideoEnd(true);
        if (testCaseInfo?.modalities?.modality_type === 'interactive_video') {
            if (sideQuestionRef.current && sideQuestionRef.current.onSubmitAnswer) {
                sideQuestionRef.current.onSubmitAnswer().then(() => {
                    onFinish();
                })
            }
        }
    }

    const renderHeaderNumber = () => {
        return <div className={'test-view-header-number'}>{testSetInfo.name}</div>
    }

    const renderNav = () => {

        return (
            <div className={'test-view-action-buttons'}>
                <Button className='me-10 test-previous-finish' variant="contained" color="primary" onClick={() => onFinish()}>
                    <span className={'test-action-btn-label'}>{testCaseInfo?.modalities?.modality_type === 'video_lecture'
                        ? (isEmptyQuizTruth ? 'Finish' : 'Submit')
                        : (testCaseInfo?.modalities?.modality_type !== 'presentations' ? 'Submit' : 'Finish')}</span>
                    <CheckCircleOutlineIcon size="small" />
                </Button>
                {
                    (testCaseInfo?.modalities?.modality_type !== 'presentations' && complete) &&
                    <Button className={'ms-20 me-10 test-previous-scores'} variant="contained" color="primary"
                        onClick={() => navigate('/main/attempt/' + attemptId + '/score')}>
                        <span className={'test-action-btn-label'}>Scores</span>
                        <HistoryOutlinedIcon size="small" />
                    </Button>
                }
                {   testSetsDetailInfo.test_set_instruction && (
                        <Button className={'ms-20 me-10 test-previous-info'} variant="contained" color="primary" onClick={() => setIsShowInstructionModal(true)}>
                            <span className={'test-action-btn-label'}>Instructions</span>
                            <InfoOutlinedIcon size="small"/>
                        </Button>
                    )
                }
                {
                    (!complete && LBLTModuleList.indexOf(testSetInfo.test_set_code) !== -1) &&
                    <Button className={'ms-20 me-10 test-previous-info'} variant="contained" color="primary" onClick={() => setIsShowInstructionModal(true)}>
                        <span className={'test-action-btn-label'}>LT Pathology Classification</span>
                        <InfoOutlinedIcon size="small" />
                    </Button>
                }
                <Button variant="contained" color="primary" className={'test-home-btn'} onClick={() => { logMainAndSubActivitiesEnd("Home"); navigate('/main'); }}>
                    <span className={'test-action-btn-label'}>Home</span>
                    <HomeOutlinedIcon size="small" />
                </Button>
            </div>
        );
    }

    const renderTestContent = () => {

        const fileExt = testSetInfo?.test_set_discussion?.split('.').pop();
        if (fileExt === 'ppt' || fileExt === 'pptx') {
            return (
                <PowerPointViewer
                    filePath={testSetInfo.test_set_discussion}
                    allowSkip={allowSkip}
                    onEnded={() => handleVideoEnded()}
                />
            )
        } else {
            return (
                <ReactPlayer
                    ref={playerRef}
                    url={testSetInfo.test_set_discussion}
                    playing={playing}
                    controls
                    width={'100%'}
                    height={'100%'}
                    config={{
                        file: {
                            attributes: {
                                disablePictureInPicture: true,
                                controlsList: 'nodownload noplaybackrate',
                                onContextMenu: e => e.preventDefault()
                            }
                        }
                    }}
                    onProgress={(e) => handleVideoProgress(e)}
                    onSeek={(time) => handleVideoSeek(time)}
                    onEnded={() => handleVideoEnded()}
                />
            )
        }
    }

    if (loading) {
        return (<RctSectionLoader style={{ backgroundColor: 'black' }} />);
    }
    return (
        <div className="viewer">
            <div id="toolbar" className={'d-flex'}>
                {/* Empty div for UI layout*/}
                <div className={'flex-fill'} />
                {renderHeaderNumber()}
                <div className={'d-flex flex-fill flex-row justify-content-end align-items-center'}>
                    {renderNav()}
                </div>
            </div>
            <div className={'test-content'}>
                {testCaseInfo && <div className={'image-container'}>
                    <div className={'video-container'}>
                        {renderTestContent()}
                    </div>
                    {testCaseInfo?.modality_type && <CommentInfo
                        complete={complete}
                        isPostTest={false}
                    />}
                </div>}
                {
                    (testCaseInfo?.modalities?.modality_type === 'interactive_video' || isVideoEnd) &&
                    <SideQuestions
                        modalityInfo={testCaseInfo?.modalities}
                        test_case_id={testCaseId}
                        ref={sideQuestionRef}
                        attempts_id={attemptId}
                        complete={complete}
                        isTruth={false}  // for covid question(truth left panel or answer right panel)
                        isPostTest={false}
                        playerRef={playerRef} // for interactive video
                        onChangePlaying={(v) => setPlaying(v)}  // for interactive video
                        questions={testCaseInfo ? testCaseInfo?.questions : null}
                    />
                }
            </div>
            <div className={'rotate-error'}>
                <img src={require('Assets/img/rotate.png')} alt='' />
            </div>
            <InstructionModal
                isOpen={isShowInstructionModal}
                onClose={() => setIsShowInstructionModal(false)}
                theme={'black'}
                type={testSetsDetailInfo.test_set_instruction ? "SELF-INSTRUCTION" : (LBLTModuleList.indexOf(testSetInfo.test_set_code) ? 'LBLT' : testCaseInfo?.modalities?.instruction_type)}
                video={testSetsDetailInfo.test_set_instruction ? {thumbnail: null, link: testSetsDetailInfo.test_set_instruction} : { thumbnail: testCaseInfo?.modalities?.instruction_video_thumbnail, link: testCaseInfo?.modalities?.instruction_video }}
            />
            {nextTestSetInfo && <NextModuleModal
                isOpen={isNextModuleModalOpen}
                onClose={onNextModuleModalClose}
                onStart={onStartNextModule}
                theme={'black'}
                testSetInfo={nextTestSetInfo}
            />}
        </div>
    )
}

export default VideoView;